/**
 * Composable for shared item display logic
 * Used by ProductItemRenderer, ComboItemRenderer, and CustomComboItemRenderer
 */

import type { ModifierSelection, CartItemModifier } from '~/types/commerce'

/**
 * Check if a modifier has any selections
 */
export function hasModifierSelections(modifier: CartItemModifier | any): boolean {
  if (!modifier?.selected) return false

  if (Array.isArray(modifier.selected)) {
    return modifier.selected.length > 0
  }

  return true
}

/**
 * Get normalized selections array from a modifier
 * Handles both single selection and array of selections
 */
export function getModifierSelections(modifier: CartItemModifier | any): ModifierSelection[] {
  if (!modifier?.selected) return []

  return Array.isArray(modifier.selected) ? modifier.selected : [modifier.selected]
}

/**
 * Format modifier price for display
 * @param price - The price to format
 * @param showZero - Whether to show zero prices
 */
export function formatModifierPrice(price: number, showZero: boolean = false): string {
  const formattedPrice = usePriceFormatter(price)

  if (price > 0) {
    return `+${formattedPrice}`
  }

  return showZero ? formattedPrice : ''
}

/**
 * Determine if quantity multiplication should be shown
 * Used for custom combos where item quantity affects modifier pricing
 */
export function shouldShowQuantityMultiplication(
  itemType: string,
  itemQuantity: number,
  productQuantity: number = 1,
): boolean {
  return itemType === 'CUSTOM_COMBO' && productQuantity > 1
}

/**
 * Format quantity multiplication display
 */
export function formatQuantityMultiplication(
  basePrice: number,
  quantity: number,
): string {
  const total = basePrice * quantity
  return `x ${quantity} = ${usePriceFormatter(total)}`
}

/**
 * Get the appropriate modifiers array from various possible locations
 * Handles the different data structures between cart and order items
 */
export function getItemModifiers(item: any): CartItemModifier[] {
  // For products, check multiple possible locations
  if (item.type === 'PRODUCT') {
    return item.modifiers
      || item.items?.[0]?.modifiers
      || item.product_data?.modifiers
      || item.product_data?.items?.[0]?.modifiers
      || []
  }

  // For combos, modifiers are typically on the items
  return []
}

/**
 * Get selected modifiers from a combo/custom combo product
 * Returns either the modifiers array or selected_modifiers object
 */
export function getComboProductModifiers(product: any): any {
  return product.modifiers || product.selected_modifiers || null
}

/**
 * Check if an item has any customizations (modifiers or legacy custom items)
 */
export function hasCustomizations(item: any): boolean {
  if (item.type === 'PRODUCT') {
    const modifiers = getItemModifiers(item)
    const hasModifiers = modifiers.some(m => hasModifierSelections(m))
    const hasCustomItems = item.product_data?.custom?.length > 0
    return hasModifiers || hasCustomItems
  }
  return false
}

/**
 * Composable wrapper for reactive usage
 */
export function useItemDisplay() {
  return {
    hasModifierSelections,
    getModifierSelections,
    formatModifierPrice,
    shouldShowQuantityMultiplication,
    formatQuantityMultiplication,
    getItemModifiers,
    getComboProductModifiers,
    hasCustomizations,
  }
}
